import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  CircleHelp,
  CircleSlash,
  Copy,
  FileText,
  Lightbulb,
  Link2Off,
  RefreshCw,
  Sparkles,
  TriangleAlert,
  Wand2,
  X,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  IconTile,
  Modal,
  SegmentedControl,
  Spinner,
  StatTile,
  type Segment,
} from '@/components/ui';
import { useAdmin } from '@/hooks/useAdmin';
import { supabase } from '@/lib/supabase';
import { cn, formatDate } from '@/lib/utils';
import {
  MAX_LUCKOR_PER_KALLA,
  commitGapsFor,
  dismissGap,
  draftGapsFor,
  resolveGap,
  triageGaps,
  type GapBedomning,
  type GapDraft,
  type GapKlass,
} from '@/lib/api';

/**
 * Luck-triagen.
 *
 * Kön är liten och välskött — 86 av 95 loggade luckor var redan hanterade när
 * den här vyn skrevs. Vad som kostade tid var inte att stänga dem, utan att
 * sortera dem: 41 av 86 visade sig vara sådant kunskapsbasen inte ska svara på
 * alls. Vyn gör den sorteringen i förväg och motiverar varje bedömning, så att
 * arbetet blir att GRANSKA ett förslag i stället för att läsa varje fråga kall.
 *
 * Dubbletter avgörs av mätning (cosinuslikhet), inte av språkmodellen, och
 * likheten visas i klartext — ett påstående man kan bedöma är värt mer än ett
 * man måste lita på.
 */

/**
 * Triagen kostar ett AI-anrop. Resultatet sparas därför i sessionStorage så att
 * en navigering mellan flikarna i Granskning inte debiterar om samma sortering.
 *
 * sessionStorage och inte en modulvariabel: React Compiler-reglerna förbjuder
 * både omtilldelning OCH mutation av något som deklarerats utanför komponenten.
 * En useRef hade inte hjälpt heller, eftersom vyn avmonteras när man byter flik.
 * Lagringen är dessutom ärligare — den lever exakt så länge webbläsarfliken gör,
 * och töms vid varje åtgärd som ändrar kön så att en inaktuell sortering aldrig
 * kan visas.
 *
 * Läsning och skrivning är try/catch:ade: i privat läge och med blockerade
 * webbplatsdata KASTAR själva åtkomsten, inte bara returnerar tomt.
 */
const CACHE_NYCKEL = 'elvis:luck-triage';

function lasCache(): GapBedomning[] | null {
  try {
    const raa = sessionStorage.getItem(CACHE_NYCKEL);
    if (!raa) return null;
    const tolkad = JSON.parse(raa);
    return Array.isArray(tolkad) ? (tolkad as GapBedomning[]) : null;
  } catch {
    return null;
  }
}

function skrivCache(v: GapBedomning[] | null) {
  try {
    if (v === null) sessionStorage.removeItem(CACHE_NYCKEL);
    else sessionStorage.setItem(CACHE_NYCKEL, JSON.stringify(v));
  } catch {
    /* utan cache blir sorteringen bara omkörd — inget går sönder */
  }
}

const KLASSER: Record<
  GapKlass,
  {
    etikett: string;
    ikon: typeof Lightbulb;
    tone: 'warning' | 'info' | 'neutral' | 'brand';
    variant: 'warning' | 'info' | 'neutral' | 'brand';
    beskrivning: string;
  }
> = {
  kunskapslucka: {
    etikett: 'Kunskapslucka',
    ikon: Lightbulb,
    tone: 'warning',
    variant: 'warning',
    beskrivning: 'Kunskapsbasen saknar verkligen svaret. Här gör en ny källa nytta.',
  },
  kontexttapp: {
    etikett: 'Kontexttapp',
    ikon: Link2Off,
    tone: 'info',
    variant: 'info',
    beskrivning:
      'Uppföljningsfråga som tappat sin tråd. Ingen ny källa hjälper — felet sitter i samtalet.',
  },
  dubblett: {
    etikett: 'Dubblett',
    ikon: Copy,
    tone: 'neutral',
    variant: 'neutral',
    beskrivning: 'Samma fråga som en redan hanterad, avgjort på uppmätt likhet.',
  },
  utanfor: {
    etikett: 'Utanför uppdraget',
    ikon: CircleSlash,
    tone: 'neutral',
    variant: 'neutral',
    beskrivning: 'Inte något kunskapsbasen ska svara på.',
  },
  obedomd: {
    etikett: 'Obedömd',
    ikon: CircleHelp,
    tone: 'neutral',
    variant: 'neutral',
    beskrivning: 'Klassningen kördes men gav inget giltigt svar. Bedöm för hand.',
  },
  osorterad: {
    etikett: 'Sorteras…',
    ikon: CircleHelp,
    tone: 'neutral',
    variant: 'neutral',
    beskrivning: 'Ännu inte klassad. Listan visas rå ur databasen medan AI:n arbetar.',
  },
};

type Filter = 'alla' | GapKlass;

export function GapsView() {
  const { isAdmin } = useAdmin();
  const [rader, setRader] = useState<GapBedomning[]>(() => lasCache() ?? []);
  const [laddar, setLaddar] = useState(() => lasCache() === null);
  const [fel, setFel] = useState<string | null>(null);
  const [kvarAttEmbedda, setKvarAttEmbedda] = useState(0);
  /** Sant först när en KLASSNING lyckats. Skiljer "inga luckor" från "vi vet inte". */
  const [arSorterad, setArSorterad] = useState(() => lasCache() !== null);
  const [filter, setFilter] = useState<Filter>('alla');
  const [valda, setValda] = useState<Set<string>>(new Set());
  const [utkastFor, setUtkastFor] = useState<GapBedomning[] | null>(null);
  const [arbetar, setArbetar] = useState<string | null>(null);
  const kordEnGang = useRef(false);
  /**
   * Speglar `rader` synkront.
   *
   * `setRader`-updatern körs vid nästa render, inte direkt, så ett värde som
   * sätts inuti den är fortfarande osatt när funktionen returnerar. Den första
   * versionen läste ett sådant värde och fick alltid `false` — med följden att
   * "sorterad" kunde stå kvar som sant medan listan visade råa rader. Alltså
   * exakt den falska gröna bock resten av vyn är byggd för att undvika.
   */
  const raderRef = useRef<GapBedomning[]>([]);

  /**
   * Rå kö direkt ur databasen, utan AI.
   *
   * Körs alltid först. Triagen kan fallera — nyckel saknas, modellen svarar
   * konstigt, nätet glappar — och då ska kön ändå synas. Ett verktyg som visar
   * "inga öppna luckor" när det egentligen inte kunde fråga är farligare än ett
   * som inte finns: det säger aktivt att allt är i ordning.
   */
  const laddaRatt = async () => {
    // supabase-js kastar inte — felet ligger i `error`. Sväljs det blir `data`
    // null, listan tom, och vyn hade kunnat visa "Inga öppna luckor" för en
    // läsning som aldrig lyckades.
    const { data, error } = await supabase
      .from('ai_unanswered')
      .select(
        'id, question_text, outcome, gaps_text, top_match_filename, top_match_similarity, created_at',
      )
      .eq('gap_status', 'open')
      .in('outcome', ['unanswered', 'partial'])
      .order('created_at', { ascending: false })
      .limit(120);
    if (error) {
      setFel(`Kön kunde inte läsas ur databasen: ${error.message}`);
      return false;
    }
    const raa: GapBedomning[] = (data ?? []).map((r) => ({
      ...(r as Omit<GapBedomning, 'klass' | 'motivering' | 'sakerhet' | 'signaler'>),
      // 'osorterad', inte 'obedomd' — raden har inte bedömts ännu, och att
      // påstå att bedömningen misslyckats medan den fortfarande pågår är
      // exakt samma sorts osanning som den falska gröna bocken.
      klass: 'osorterad' as const,
      motivering: '',
      sakerhet: 'lag' as const,
      signaler: { ord: 0, borjarMedSyftning: false, saknarEgennamn: false, harSvagTraff: false },
    }));
    // Ersätter bara när vi inte redan har något att visa. En färdig sortering
    // blir inte ogiltig av att en OMkörning misslyckades. Beslutet tas mot
    // ref:en, som är aktuell nu — inte mot en updater som körs senare.
    if (raderRef.current.length > 0) return false;
    raderRef.current = raa;
    setRader(raa);
    return raa.length > 0;
  };

  const kor = async () => {
    setLaddar(true);
    setFel(null);
    try {
      const svar = await triageGaps();
      skrivCache(svar.bedomningar);
      raderRef.current = svar.bedomningar;
      setRader(svar.bedomningar);
      setKvarAttEmbedda(svar.pendingEmbeddings);
      setArSorterad(true);
      // En delvis lyckad sortering är inte ett tyst utfall — säg vad som föll.
      setFel(
        svar.klassningsFel
          ? `Klassningen blev inte komplett: ${svar.klassningsFel}. Raderna märkta "Obedömd" bär var och en sin egen förklaring — kapades svaret räcker det att sortera om.`
          : null,
      );
    } catch (e) {
      setFel((e as Error).message);
      skrivCache(null);
      // Kön ska synas även när sorteringen inte gick att göra.
      const ersatte = await laddaRatt();
      // Bara det som faktiskt VISAS avgör. Står listan tom, eller visar den råa
      // rader, är ingenting sorterat — och då får rubriken inte påstå det.
      if (ersatte || raderRef.current.length === 0) setArSorterad(false);
    } finally {
      setLaddar(false);
    }
  };

  useEffect(() => {
    if (kordEnGang.current) return;
    kordEnGang.current = true;
    if (lasCache()) return;
    void (async () => {
      await laddaRatt();
      await kor();
    })();
    // Ska köras exakt en gång vid montering; kor/laddaRatt skapas om varje render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const raknare = useMemo(() => {
    const r: Record<GapKlass, number> = {
      kunskapslucka: 0,
      kontexttapp: 0,
      dubblett: 0,
      utanfor: 0,
      obedomd: 0,
      osorterad: 0,
    };
    for (const b of rader) r[b.klass]++;
    return r;
  }, [rader]);

  const synliga = useMemo(
    () =>
      filter === 'alla'
        ? rader
        : rader.filter((b) => b.klass === filter),
    [rader, filter],
  );

  /**
   * Valda rader som FORTFARANDE får ett utkast.
   *
   * `valda` överlever en omsortering. En rad som klassas om från kunskapslucka
   * till dubblett tappar sin kryssruta och går inte att avmarkera — men låg
   * kvar i urvalet, och massåtgärden hade stängt den mot en källa som inte
   * skrevs för den. Filtret här är det som hindrar det.
   */
  const valdaRader = useMemo(
    () =>
      rader.filter(
        (b) => valda.has(b.id) && (b.klass === 'kunskapslucka' || b.klass === 'obedomd'),
      ),
    [rader, valda],
  );

  const segment: Segment<Filter>[] = [
    { value: 'alla', label: 'Alla', count: rader.length },
    { value: 'kunskapslucka', label: 'Kunskapsluckor', count: raknare.kunskapslucka },
    { value: 'kontexttapp', label: 'Kontexttapp', count: raknare.kontexttapp },
    { value: 'dubblett', label: 'Dubbletter', count: raknare.dubblett },
    { value: 'utanfor', label: 'Utanför', count: raknare.utanfor },
  ];
  if (raknare.obedomd > 0) {
    segment.push({ value: 'obedomd', label: 'Obedömda', count: raknare.obedomd });
  }
  if (raknare.osorterad > 0) {
    segment.push({ value: 'osorterad', label: 'Sorteras', count: raknare.osorterad });
  }

  /**
   * Åtgärda EN lucka.
   *
   * Raden tas bort lokalt i stället för att hela triagen körs om. En omkörning
   * hade kostat en ny Voyage-batch och ett nytt Claude-anrop per klick — att
   * beta av fyrtio irrelevanta luckor hade alltså blivit fyrtio fulla
   * sorteringar. De kvarvarande bedömningarna påverkas inte av att en rad
   * försvinner, så de får stå kvar; cachen skrivs om till det som faktiskt visas.
   */
  const utforPa = async (rad: GapBedomning, fn: (id: string) => Promise<unknown>) => {
    setArbetar(rad.id);
    try {
      await fn(rad.id);
      setValda((v) => {
        const n = new Set(v);
        n.delete(rad.id);
        return n;
      });
      const kvarvarande = raderRef.current.filter((b) => b.id !== rad.id);
      raderRef.current = kvarvarande;
      // Cachen speglar listan bara när den faktiskt är sorterad.
      skrivCache(arSorterad ? kvarvarande : null);
      setRader(kvarvarande);
    } catch (e) {
      setFel((e as Error).message);
    } finally {
      setArbetar(null);
    }
  };

  if (laddar && rader.length === 0) {
    return (
      <Card className="p-14 text-center">
        <Spinner size={26} className="text-brand-500 mx-auto mb-4" />
        <h3 className="text-display text-[17px] text-ink-900">AI:n sorterar luckorna…</h3>
        <p className="text-[12.5px] text-ink-500 mt-1.5">
          Dubbletter mäts först · resten bedöms av språkmodellen
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* ---- Nyckeltal ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          label="Öppna luckor"
          value={rader.length}
          icon={<CircleHelp size={13} strokeWidth={2.25} />}
          tone="neutral"
          hint={rader.length === 0 ? 'inget att göra' : 'i kön just nu'}
        />
        <StatTile
          label="Kunskapsluckor"
          value={arSorterad ? raknare.kunskapslucka : '–'}
          icon={<Lightbulb size={13} strokeWidth={2.25} />}
          tone="warning"
          hint={arSorterad ? 'ny källa gör nytta' : 'ej sorterat'}
        />
        <StatTile
          label="Kontexttapp"
          value={arSorterad ? raknare.kontexttapp : '–'}
          icon={<Link2Off size={13} strokeWidth={2.25} />}
          tone="info"
          hint={arSorterad ? 'ingen källa hjälper' : 'ej sorterat'}
        />
        <StatTile
          label="Kan avfärdas"
          value={arSorterad ? raknare.dubblett + raknare.utanfor : '–'}
          icon={<CircleSlash size={13} strokeWidth={2.25} />}
          tone="neutral"
          hint={arSorterad ? 'dubbletter och utanför' : 'ej sorterat'}
        />
      </div>

      {fel && (
        <Card className="p-3.5 border-red-200 bg-red-50">
          <p className="text-[12.5px] text-red-800 font-semibold">
            {arSorterad ? fel : `AI-sorteringen kunde inte köras: ${fel}`}
          </p>
          {!arSorterad && (
            <p className="text-[12px] text-red-700 mt-1 leading-relaxed">
              Luckorna nedan visas <strong>osorterade</strong> — de är hämtade direkt ur databasen.
              Att ett fack står tomt betyder här att sorteringen inte gjorts, inte att facket är
              tomt.
            </p>
          )}
        </Card>
      )}

      {kvarAttEmbedda > 0 && (
        <Card className="p-3.5 border-amber-200 bg-amber-50">
          <p className="text-[12.5px] text-amber-900 leading-relaxed">
            <strong>{kvarAttEmbedda} frågor hann inte förberedas</strong> i den här omgången.
            Dubblettkontrollen jämför därför mot ett ofullständigt referensmaterial — en dubblett kan
            ha missats och ligga kvar som kunskapslucka. Kör sorteringen igen så tas resten med.
          </p>
        </Card>
      )}

      {/* ---- Listan ---- */}
      <Card>
        <div className="flex items-start justify-between gap-4 flex-wrap p-4 pb-3">
          <div className="min-w-0">
            <h2 className="text-display text-[15px] text-ink-900">Öppna luckor</h2>
            <p className="text-[12px] font-medium text-ink-500 mt-1">
              {rader.length} frågor ELvis inte kunde besvara
              {arSorterad ? ' · sorterade av AI, dubbletter uppmätta' : ' · ännu inte sorterade'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<RefreshCw size={13} strokeWidth={2.25} className={cn(laddar && 'animate-spin')} />}
              onClick={() => void kor()}
              disabled={laddar}
            >
              Sortera om
            </Button>
          </div>
        </div>

        <div className="px-4 pb-3 overflow-x-auto">
          <SegmentedControl
            segments={segment}
            value={filter}
            onChange={(v) => setFilter(v)}
            label="Filtrera luckor på bedömning"
          />
        </div>

        {/* Massåtgärd: ett utkast som täcker flera luckor på en gång */}
        {isAdmin && valdaRader.length > 0 && (
          <div className="mx-4 mb-3 flex items-center justify-between gap-3 flex-wrap px-3.5 py-2.5 rounded-lg bg-brand-50">
            <p className="text-[12.5px] font-semibold text-brand-700">
              {valdaRader.length} luckor valda
              {valdaRader.length >= MAX_LUCKOR_PER_KALLA && (
                <span className="font-medium">
                  {' '}
                  · taket är {MAX_LUCKOR_PER_KALLA} per källa, så att inget stängs mot en text som
                  inte sett det
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setValda(new Set())}>
                Avmarkera
              </Button>
              <Button
                size="sm"
                leftIcon={<Wand2 size={13} strokeWidth={2.25} />}
                onClick={() => setUtkastFor(valdaRader)}
              >
                Skriv EN källa för alla
              </Button>
            </div>
          </div>
        )}

        {synliga.length === 0 ? (
          <div className="px-4 py-14 text-center border-t border-ink-100">
            <IconTile
              icon={
                rader.length === 0 && !arSorterad ? (
                  <TriangleAlert size={19} strokeWidth={2} />
                ) : (
                  <CheckCircle2 size={19} strokeWidth={2} />
                )
              }
              tone={rader.length === 0 && !arSorterad ? 'warning' : 'success'}
              size="lg"
              className="mx-auto"
            />
            <h3 className="text-display text-[16px] text-ink-900 mt-3.5 mb-1.5">
              {rader.length > 0
                ? 'Inget i det här facket'
                : arSorterad
                  ? 'Inga öppna luckor'
                  : 'Kunde inte läsa kön'}
            </h3>
            <p className="text-[12.5px] text-ink-500 max-w-md mx-auto leading-relaxed">
              {rader.length > 0
                ? 'Byt fack ovan för att se de övriga luckorna.'
                : arSorterad
                  ? 'ELvis klarar alla frågor med nuvarande kunskapsbas. Nya luckor dyker upp här när någon ställer en fråga som inte kan besvaras.'
                  : 'Listan kunde varken sorteras eller hämtas. Det betyder INTE att kön är tom — försök igen med Sortera om.'}
            </p>
          </div>
        ) : (
          <ul className="border-t border-ink-100">
            {synliga.map((rad) => (
              <TriageRad
                key={rad.id}
                rad={rad}
                isAdmin={isAdmin}
                vald={valda.has(rad.id)}
                arbetar={arbetar === rad.id}
                onValj={() =>
                  setValda((v) => {
                    const n = new Set(v);
                    if (n.has(rad.id)) n.delete(rad.id);
                    else if (n.size < MAX_LUCKOR_PER_KALLA) n.add(rad.id);
                    return n;
                  })
                }
                onUtkast={() => setUtkastFor([rad])}
                onAtgardad={() => void utforPa(rad, resolveGap)}
                onAvfarda={() => void utforPa(rad, dismissGap)}
              />
            ))}
          </ul>
        )}
      </Card>

      {utkastFor && (
        <GapDraftModal
          luckor={utkastFor}
          onClose={() => setUtkastFor(null)}
          onKlar={() => {
            setUtkastFor(null);
            setValda(new Set());
            skrivCache(null);
            void kor();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function TriageRad({
  rad,
  isAdmin,
  vald,
  arbetar,
  onValj,
  onUtkast,
  onAtgardad,
  onAvfarda,
}: {
  rad: GapBedomning;
  isAdmin: boolean;
  vald: boolean;
  arbetar: boolean;
  onValj: () => void;
  onUtkast: () => void;
  onAtgardad: () => void;
  onAvfarda: () => void;
}) {
  const [oppen, setOppen] = useState(false);
  const meta = KLASSER[rad.klass];
  const Ikon = meta.ikon;
  const kanUtkast = rad.klass === 'kunskapslucka' || rad.klass === 'obedomd';
  const sorteras = rad.klass === 'osorterad';

  return (
    <li className={cn('border-b border-ink-100 last:border-b-0', vald && 'bg-brand-50/40')}>
      <div className="flex items-start gap-3 px-4 py-3.5">
        {isAdmin && kanUtkast && (
          <input
            type="checkbox"
            checked={vald}
            onChange={onValj}
            className="mt-1 w-3.5 h-3.5 rounded border-ink-300 text-brand-500 focus:ring-brand-500 shrink-0 cursor-pointer"
            aria-label={`Välj: ${rad.question_text.slice(0, 60)}`}
          />
        )}
        <IconTile icon={<Ikon size={14} strokeWidth={2.25} />} tone={meta.tone} size="md" />

        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setOppen((o) => !o)}
            className="text-left w-full group"
            aria-expanded={oppen}
          >
            <p
              className={cn(
                'text-[13.5px] font-semibold text-ink-900 leading-snug group-hover:text-brand-700 transition-colors',
                !oppen && 'line-clamp-2',
              )}
            >
              {rad.question_text}
            </p>
          </button>

          {rad.motivering && (
            <p className="text-[12px] text-ink-500 mt-1 leading-relaxed">{rad.motivering}</p>
          )}

          <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[10.5px] font-semibold text-ink-500">
            <span>
              {formatDate(rad.created_at, {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {rad.top_match_similarity != null && (
              <>
                <span className="opacity-40">·</span>
                <span className="tabular-nums">
                  närmast i KB {Math.round(rad.top_match_similarity * 100)} %
                </span>
              </>
            )}
            {rad.sakerhet !== 'hog' && (
              <>
                <span className="opacity-40">·</span>
                <span className={cn(rad.sakerhet === 'lag' && 'text-amber-700')}>
                  {rad.sakerhet === 'lag' ? 'osäker bedömning' : 'medelsäker'}
                </span>
              </>
            )}
          </div>

          {oppen && (
            <div className="mt-3 space-y-2.5">
              <p className="text-[11.5px] text-ink-600 leading-relaxed p-3 rounded-lg bg-ink-50">
                <span className="text-metric-label block mb-1">Varför det här facket</span>
                {meta.beskrivning}
              </p>

              {rad.klass === 'dubblett' && rad.dubblettAv && (
                <div className="p-3 rounded-lg bg-ink-50">
                  <span className="text-metric-label block mb-1.5">
                    Samma fråga som (likhet {rad.likhet?.toFixed(2)})
                  </span>
                  <p className="text-[12px] text-ink-700 leading-snug">{rad.dubblettAv.fraga}</p>
                  <p className="text-[11px] text-ink-500 mt-1.5 font-semibold">
                    {formatDate(rad.dubblettAv.datum, { day: 'numeric', month: 'short', year: 'numeric' })}
                    {' · '}
                    {rad.dubblettAv.status === 'resolved' ? 'åtgärdad' : 'avfärdad'}
                    {rad.dubblettAv.kalla ? ` · källa: ${rad.dubblettAv.kalla}` : ''}
                  </p>
                </div>
              )}

              {rad.klass === 'kontexttapp' && (
                <div className="p-3 rounded-lg bg-ink-50">
                  <span className="text-metric-label block mb-1.5">Signaler som avgjorde</span>
                  <ul className="text-[11.5px] text-ink-600 space-y-0.5">
                    <li>{rad.signaler.ord} ord</li>
                    {rad.signaler.borjarMedSyftning && <li>börjar med ett syftningsord</li>}
                    {rad.signaler.saknarEgennamn && <li>inga egennamn att söka på</li>}
                    {rad.signaler.harSvagTraff && <li>svag träff i kunskapsbasen</li>}
                  </ul>
                </div>
              )}

              {rad.gaps_text && (
                <p className="text-[12px] text-amber-900 bg-amber-50 rounded-lg px-3 py-2 leading-relaxed">
                  {rad.gaps_text}
                </p>
              )}

              {rad.top_match_filename && (
                <p className="text-[11px] text-ink-500 font-semibold truncate">
                  Närmast i kunskapsbasen: {rad.top_match_filename}
                </p>
              )}
            </div>
          )}

          {isAdmin && !sorteras && (
            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
              {kanUtkast && (
                <Button
                  size="sm"
                  leftIcon={<Wand2 size={13} strokeWidth={2.25} />}
                  onClick={onUtkast}
                  disabled={arbetar}
                >
                  Skapa utkast
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<CheckCircle2 size={13} strokeWidth={2.25} />}
                onClick={onAtgardad}
                disabled={arbetar}
                title="Luckan är hanterad eller besvarad på annat sätt"
              >
                Markera åtgärdad
              </Button>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<X size={13} strokeWidth={2.25} />}
                onClick={onAvfarda}
                disabled={arbetar}
                title="Frågan är inte relevant för oss"
              >
                Avfärda
              </Button>
              {arbetar && <Spinner size={13} className="text-ink-500" />}
            </div>
          )}
        </div>

        <Badge variant={meta.variant} className="shrink-0 mt-0.5">
          {meta.etikett}
        </Badge>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ */

function GapDraftModal({
  luckor,
  onClose,
  onKlar,
}: {
  luckor: GapBedomning[];
  onClose: () => void;
  onKlar: () => void;
}) {
  const [fas, setFas] = useState<'skriver' | 'klar' | 'sparar'>('skriver');
  const [utkast, setUtkast] = useState<GapDraft | null>(null);
  const [titel, setTitel] = useState('');
  const [innehall, setInnehall] = useState('');
  const [fel, setFel] = useState<string | null>(null);
  const titelId = useId();
  const innehallId = useId();

  const generera = async () => {
    setFas('skriver');
    setFel(null);
    try {
      const d = await draftGapsFor(luckor.map((l) => l.id));
      setUtkast(d);
      setTitel(d.title || luckor[0].question_text.slice(0, 60));
      setInnehall(d.draft);
      setFas('klar');
    } catch (e) {
      setFel((e as Error).message);
      setFas('klar');
    }
  };

  useEffect(() => {
    void generera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spara = async () => {
    if (innehall.trim().length < 50 || !titel.trim()) return;
    setFas('sparar');
    setFel(null);
    try {
      await commitGapsFor(
        luckor.map((l) => l.id),
        titel.trim(),
        innehall,
      );
      onKlar();
    } catch (e) {
      setFel((e as Error).message);
      setFas('klar');
    }
  };

  return (
    <Modal
      open
      onClose={fas === 'sparar' ? () => {} : onClose}
      title={
        luckor.length === 1
          ? 'Skapa kunskapskälla från lucka'
          : `Skapa EN kunskapskälla för ${luckor.length} luckor`
      }
      size="2xl"
    >
      <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
        <div className="p-3.5 rounded-lg bg-ink-50">
          <span className="text-metric-label block mb-1.5">
            {luckor.length === 1 ? 'Frågan som saknade svar' : `${luckor.length} frågor källan ska täcka`}
          </span>
          <ul className="space-y-1.5">
            {luckor.slice(0, 8).map((l) => (
              <li key={l.id} className="text-[12.5px] font-semibold text-ink-800 leading-snug">
                · {l.question_text.slice(0, 180)}
                {l.question_text.length > 180 ? '…' : ''}
              </li>
            ))}
          </ul>
          {luckor.length > 8 && (
            <p className="text-[11.5px] text-ink-500 mt-1.5 font-semibold">
              … och {luckor.length - 8} till
            </p>
          )}
        </div>

        {fas === 'skriver' ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Spinner size={28} className="text-brand-500 mb-4" />
            <p className="text-display text-[17px] text-ink-900">AI:n skriver ett utkast…</p>
            <p className="text-[12px] text-ink-500 mt-1.5">
              Söker i befintlig kunskapsbas · markerar det som behöver kompletteras
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-amber-50">
              <TriangleAlert size={15} className="text-amber-700 shrink-0 mt-0.5" strokeWidth={2.25} />
              <p className="text-[12px] text-amber-900 leading-relaxed">
                <strong>AI-utkast grundat i befintlig kunskapsbas.</strong> Verifiera alla fakta och
                fyll i <code className="font-mono text-[11px]">[ATT KOMPLETTERA: …]</code>-platshållare
                mot källa innan du lägger till. AI:n hittar inte på siffror, datum eller paragrafer.
              </p>
            </div>

            {utkast && utkast.needs.length > 0 && (
              <div className="p-3.5 rounded-lg border border-ink-200">
                <span className="text-metric-label inline-flex items-center gap-1.5 mb-2">
                  <Sparkles size={11} strokeWidth={2.5} />
                  AI:n flaggar att detta bör verifieras
                </span>
                <ul className="space-y-1">
                  {utkast.needs.map((n, i) => (
                    <li
                      key={i}
                      className="text-[12.5px] text-ink-700 flex items-start gap-2 leading-snug"
                    >
                      <span className="text-amber-600 mt-1">·</span>
                      <span>{n}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <label htmlFor={titelId} className="text-metric-label block mb-1.5">
                Titel / filnamn på källan
              </label>
              <input
                id={titelId}
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg bg-surface border border-ink-200 text-[13px] font-semibold text-ink-900 focus:border-ink-900 focus:ring-2 focus:ring-ink-100 focus:outline-none transition-all"
                placeholder="t.ex. Insamlingsmål bärbara batterier 2026"
              />
            </div>

            <div>
              <label htmlFor={innehallId} className="text-metric-label block mb-1.5">
                Innehåll (redigera fritt innan du lägger till)
              </label>
              <textarea
                id={innehallId}
                value={innehall}
                onChange={(e) => setInnehall(e.target.value)}
                rows={14}
                className="w-full p-3.5 rounded-lg bg-surface border border-ink-200 text-[13px] text-ink-800 leading-relaxed focus:border-ink-900 focus:ring-2 focus:ring-ink-100 focus:outline-none transition-all resize-y font-mono"
              />
            </div>

            {utkast && utkast.usedSources.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-metric-label inline-flex items-center gap-1">
                  <FileText size={11} strokeWidth={2.25} /> Byggde på
                </span>
                {utkast.usedSources.slice(0, 5).map((s) => (
                  <Badge key={s} variant="neutral" className="max-w-[200px] truncate normal-case tracking-normal">
                    {s.replace(/\.[^/.]+$/, '')}
                  </Badge>
                ))}
              </div>
            )}

            {fel && (
              <p className="text-[12.5px] text-red-700 font-semibold bg-red-50 rounded-lg px-3 py-2">
                {fel}
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-ink-100 bg-ink-50/50">
        <Button variant="ghost" onClick={onClose} disabled={fas === 'sparar'}>
          Avbryt
        </Button>
        <div className="flex items-center gap-2">
          {fas !== 'skriver' && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Wand2 size={13} strokeWidth={2.25} />}
              onClick={() => void generera()}
              disabled={fas === 'sparar'}
            >
              Gör om
            </Button>
          )}
          <Button
            onClick={() => void spara()}
            loading={fas === 'sparar'}
            disabled={fas === 'skriver' || innehall.trim().length < 50 || !titel.trim()}
            leftIcon={<CheckCircle2 size={15} strokeWidth={2.25} />}
          >
            {luckor.length === 1
              ? 'Godkänn & lägg till'
              : `Godkänn & stäng ${luckor.length} luckor`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
